import { merge } from "lodash"
import { useCallback } from "react"
import { Designation } from "../../types"
import { formattedCents } from "source/giving/utils/money"
import { Action } from "./reducer"
import { BLANK_FUND_ID } from "source/giving/donate/form/constants"

export type ValidationErrorMessage = string

export type ValidationErrors = {
  [key: string]: ValidationErrorMessage[]
}

const STRIPE_MINIMUM_AMOUNT_CENTS = 50
const STRIPE_MAXIMUM_AMOUNT_CENTS = 99999999

export interface RecurringDonationFormState {
  designations: Designation[]
  selectedPaymentMethodId: string | null
}

export type Dispatch = (action: Action) => void

export function useValidateRecurringDonation(
  state: RecurringDonationFormState,
  dispatch: Dispatch,
): () => boolean {
  return useCallback(() => {
    const errors = validate(state)
    const isValid = !hasErrors(errors)
    if (!isValid) {
      dispatch({ type: "START_LIVE_VALIDATION" })
    }
    return isValid
  }, [state, dispatch])
}

export function hasErrors(errors: ValidationErrors): boolean {
  return Object.keys(errors).length > 0
}

export function extractMessagesFrom(
  errors: ValidationErrors,
  ...searchKeys: string[]
): ValidationErrorMessage[] {
  const validKeys = Object.keys(errors)
  return validKeys
    .filter((vk) => searchKeys.some((searchKey) => vk.startsWith(searchKey)))
    .flatMap((k) => errors[k] || [])
}

export function validate(state: RecurringDonationFormState): ValidationErrors {
  return VALIDATORS.reduce((errors, validationFn) => {
    const result = validationFn(state)
    return merge(errors, result)
  }, {})
}

const VALIDATORS = [
  paymentMethodIdPresence,
  designationFundIdPresence,
  designationAmountPresence,
  totalAmountAboveMinimum,
  totalAmountBelowMaximum,
]

function paymentMethodIdPresence(
  state: RecurringDonationFormState,
): ValidationErrors {
  if (!state.selectedPaymentMethodId) {
    return {
      paymentMethod: ["Select a payment method"],
    }
  }
  return {}
}

function designationFundIdPresence(
  state: RecurringDonationFormState,
): ValidationErrors {
  const errors: ValidationErrors = {}
  state.designations.forEach((designation) => {
    if (!designation.fund?.id || designation.fund?.id === BLANK_FUND_ID) {
      const fundId = designation.fund?.id || BLANK_FUND_ID
      errors[`designation_${fundId}_fundId`] = ["Select a fund"]
    }
  })
  return errors
}

function designationAmountPresence(
  state: RecurringDonationFormState,
): ValidationErrors {
  const errors: ValidationErrors = {}
  state.designations.forEach((designation) => {
    const amount = designation.amount
    const numericAmount = parseFloat(amount)
    if (amount === "" || isNaN(numericAmount) || numericAmount === 0) {
      const fundId = designation.fund?.id || BLANK_FUND_ID
      errors[`designation_${fundId}_amount`] = ["Enter an amount"]
    }
  })
  return errors
}

function designationTotalCents(designations: Designation[]): number {
  return designations.reduce((totalCents, designation) => {
    const amountInCents =
      Math.round(parseFloat(designation.amount || "0") * 100) || 0
    return totalCents + amountInCents
  }, 0)
}

function totalAmountAboveMinimum(
  state: RecurringDonationFormState,
): ValidationErrors {
  const totalCents = designationTotalCents(state.designations)
  const allAmountsBlank = state.designations.every(
    (d) => d.amount === "" || d.amount === "0",
  )

  if (!allAmountsBlank && totalCents < STRIPE_MINIMUM_AMOUNT_CENTS) {
    return {
      totalAmount: [
        `Amount must be at least ${formattedCents(STRIPE_MINIMUM_AMOUNT_CENTS)}`,
      ],
    }
  }
  return {}
}

function totalAmountBelowMaximum(
  state: RecurringDonationFormState,
): ValidationErrors {
  const totalCents = designationTotalCents(state.designations)
  if (totalCents > STRIPE_MAXIMUM_AMOUNT_CENTS) {
    return {
      totalAmount: [
        `Amount must be below ${formattedCents(STRIPE_MAXIMUM_AMOUNT_CENTS)}`,
      ],
    }
  }
  return {}
}
